import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { ArrowRight, Download, Loader2, X } from "lucide-react";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_KEYS = [
  { key: "name", type: "text", required: true },
  { key: "email", type: "email", required: true },
  { key: "phone", type: "tel", required: false },
];

export const Contact = ({ h1 = false }) => {
  const { t } = useLang();
  const H = h1 ? "h1" : "h2";
  const [params, setParams] = useSearchParams();
  const gradeParam = params.get("grade");
  const eventParam = params.get("event");
  const prefillLabel = gradeParam ? `Grade: ${gradeParam}` : eventParam ? `Event: ${eventParam}` : null;
  const [form, setForm] = useState(() => ({
    name: "", email: "", phone: "", website: "",
    message: gradeParam ? t.gradePg.prefill(gradeParam) : eventParam ? t.events.prefill(eventParam) : "",
  }));
  const [loading, setLoading] = useState(false);

  const clearGrade = () => {
    params.delete("grade");
    params.delete("event");
    setParams(params, { replace: true });
    setForm((f) => ({ ...f, message: "" }));
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error(t.contact.toastRequired);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success(t.contact.toastSuccess);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error(t.contact.toastFail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-clara-espresso text-clara-cream py-24 lg:py-36 noise-overlay relative">
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="eyebrow text-clara-terracotta mb-6">{t.contact.eyebrow}</p>
              <H className="font-display font-light text-5xl lg:text-7xl leading-[0.92] tracking-tighter mb-8">
                {t.contact.headingA} <br />{t.contact.headingB}<span className="italic text-clara-terracotta">{t.contact.headingEm}</span>
              </H>
              <p className="text-clara-darkmuted leading-relaxed max-w-md mb-10">
                {t.contact.intro}
              </p>
              <div className="space-y-2 font-data text-sm text-clara-cream/80">
                <div>marketing@tanputratama.co.id</div>
                <div>+6221 826 10031 / +6221 825 1113</div>
                <div className="text-clara-darkmuted max-w-xs pt-2">Jl. Raya Narogong Pangkalan 2, Cikiwul, Bantargebang, Bekasi 17152, Indonesia</div>
              </div>
              <a
                href="/clara-catalogue.pdf"
                download
                data-testid="contact-catalogue-btn"
                className="group mt-10 inline-flex items-center gap-4 border border-clara-cream/20 px-6 py-5 hover:border-clara-terracotta transition-colors duration-300"
              >
                <Download className="w-6 h-6 text-clara-terracotta shrink-0" strokeWidth={1.5} />
                <span>
                  <span className="block font-data text-[10px] tracking-[0.2em] uppercase text-clara-darkmuted">Clara — PT Tan Putra Tama</span>
                  <span className="block font-display text-xl mt-1 group-hover:text-clara-terracotta transition-colors">{t.catalogueCta}</span>
                </span>
              </a>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              <form onSubmit={submit} data-testid="inquiry-form" className="max-w-xl space-y-6">
                {prefillLabel && (
                  <div data-testid="grade-prefill-note" className="inline-flex items-center gap-3 rounded-full border border-clara-terracotta/60 bg-clara-terracotta/10 px-4 py-2 font-data text-xs text-clara-cream">
                    <span>{prefillLabel}</span>
                    <button type="button" onClick={clearGrade} data-testid="grade-prefill-clear" aria-label="Clear" className="text-clara-cream/60 hover:text-clara-cream transition-colors">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
                {FIELD_KEYS.map((f) => (
                  <div key={f.key} className="flex flex-col gap-2">
                    <label htmlFor={`field-${f.key}`} className="font-data text-[11px] tracking-[0.2em] uppercase text-clara-darkmuted">
                      {t.contact.labels[f.key]}{f.required && <span className="text-clara-terracotta"> *</span>}
                    </label>
                    <input
                      id={`field-${f.key}`}
                      data-testid={`input-${f.key}`}
                      type={f.type}
                      placeholder={t.contact.placeholders[f.key]}
                      value={form[f.key]}
                      onChange={set(f.key)}
                      className="bg-clara-surface/60 border border-clara-cream/20 rounded-sm px-4 py-3 text-clara-cream placeholder:text-clara-cream/30 outline-none focus:border-clara-terracotta transition-colors"
                    />
                  </div>
                ))}

                <div className="flex flex-col gap-2">
                  <label htmlFor="field-message" className="font-data text-[11px] tracking-[0.2em] uppercase text-clara-darkmuted">
                    {t.contact.labels.message}
                  </label>
                  <textarea
                    id="field-message"
                    data-testid="input-message"
                    rows={5}
                    placeholder={t.contact.placeholders.message}
                    value={form.message}
                    onChange={set("message")}
                    className="bg-clara-surface/60 border border-clara-cream/20 rounded-sm px-4 py-3 text-clara-cream placeholder:text-clara-cream/30 outline-none focus:border-clara-terracotta transition-colors resize-none"
                  />
                </div>

                {/* Honeypot — hidden from humans; spam bots fill it and get silently dropped */}
                <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: 0, height: 0, overflow: "hidden", opacity: 0 }}>
                  <label htmlFor="field-website">Website</label>
                  <input
                    id="field-website"
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                  />
                </div>

                <button type="submit" disabled={loading} data-testid="submit-inquiry-btn" className="inline-flex items-center gap-2 rounded-full bg-clara-terracotta px-8 py-4 text-sm font-semibold text-clara-cream hover:bg-clara-rust transition-colors disabled:opacity-60">
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> {t.contact.sending}</> : <>{t.contact.submit} <ArrowRight className="w-4 h-4" /></>}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
